// Piezas compartidas por los dashboards analíticos (General, Comparador, Grupos):
// constantes, helpers, agregación por conjunto de cuentas y componentes UI
// (miniatura, tarjeta de post, modal de detalle). Reutiliza endpoints existentes.
import React, { useEffect, useState } from 'react';
import {
    FaInstagram, FaFacebook, FaTiktok, FaTwitter, FaGlobe, FaImage,
    FaHeart, FaCommentDots, FaPlay, FaExternalLinkAlt, FaTimes,
    FaUserCheck, FaEye,
} from 'react-icons/fa';
import { socialApi, proxyImage } from './api';

export const NAVY = '#073C5C';
export const ORANGE = '#F68D2E';

export const PLAT = {
    instagram: { label: 'Instagram', Icon: FaInstagram, color: '#E1306C' },
    facebook: { label: 'Facebook', Icon: FaFacebook, color: '#1877F2' },
    tiktok: { label: 'TikTok', Icon: FaTiktok, color: '#111827' },
    twitter: { label: 'Twitter / X', Icon: FaTwitter, color: '#1DA1F2' },
};

// Eje competitivo: cuentas propias vs. de interés (competencia).
export const KIND = {
    own: { label: 'Propias', Icon: FaUserCheck, color: NAVY, tint: '#e0f2fe' },
    interest: { label: 'De interés', Icon: FaEye, color: '#7c3aed', tint: '#ede9fe' },
};
export const sentDot = { pos: '#16a34a', neu: '#d97706', neg: '#dc2626' };
export const kindOf = (a) => (a?.account_kind === 'interest' ? 'interest' : 'own');

export const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    const x = Number(n);
    if (Number.isNaN(x)) return '—';
    if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(1) + 'M';
    if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
    return String(x);
};
export const round1 = (x) => Math.round(x * 10) / 10;
export const nssColor = (v) => (v == null ? '#94a3b8' : v > 15 ? '#16a34a' : v < -15 ? '#dc2626' : '#d97706');
export const dateFromDays = (days) => {
    if (!days) return '';
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};
// Media móvil para suavizar series (ignora huecos).
export const movingAvg = (vals, window) => {
    if (!window || window <= 1) return vals;
    const half = Math.floor(window / 2);
    return vals.map((_, i) => {
        let sum = 0, n = 0;
        for (let j = i - half; j <= i + half; j++) {
            const v = vals[j];
            if (v != null) { sum += v; n += 1; }
        }
        return n ? Math.round((sum / n) * 100) / 100 : null;
    });
};
export const acctHandle = (a) => (a && a.track_by === 'keyword' && a.platform === 'tiktok'
    ? a.username : `@${a?.username}`);
// Suma N días a una fecha 'YYYY-MM-DD' (UTC, sin desfase por zona horaria).
export const addDaysStr = (dateStr, n) => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

export const RANGES = [
    { label: 'Últimos 30 días', days: 30 },
    { label: 'Últimos 90 días', days: 90 },
    { label: 'Último año', days: 365 },
    { label: 'Todo', days: 0 },
];

// Agrega un conjunto de cuentas (followers, ER ponderado por posts, NSS ponderado
// por comentarios, views, flujo de sentimiento por bucket). overviews: { id: ov }.
export function aggregate(accounts, overviews) {
    const flowMap = {};
    let followers = 0, growth = 0, interactions = 0, posts = 0;
    let analyzed = 0, pos = 0, neg = 0, views = 0;
    let erNum = 0, erDen = 0;

    for (const a of accounts) {
        const ov = overviews[a.id] || {};
        const rg = ov.reach_growth || {};
        const eng = ov.engagement || {};
        const sent = ov.sentiment || {};
        const fol = a.followers ?? rg.followers ?? 0;
        const accPosts = eng.posts || 0;
        const accEr = eng.avg_engagement_rate || 0;

        followers += fol || 0;
        growth += rg.followers_growth || 0;
        interactions += (eng.total_likes || 0) + (eng.total_comments || 0);
        posts += accPosts;
        analyzed += sent.analyzed || 0;
        pos += sent.pos || 0;
        neg += sent.neg || 0;
        views += rg.total_views || 0;
        if (accPosts > 0) { erNum += accEr * accPosts; erDen += accPosts; }

        (ov.sentiment_flow || []).forEach((b) => {
            if (!b.bucket) return;
            if (!flowMap[b.bucket]) flowMap[b.bucket] = { pos: 0, neg: 0, analyzed: 0 };
            flowMap[b.bucket].pos += b.pos || 0;
            flowMap[b.bucket].neg += b.neg || 0;
            flowMap[b.bucket].analyzed += b.analyzed || 0;
        });
    }

    const evolution = Object.keys(flowMap).sort().map((b) => {
        const f = flowMap[b];
        return { bucket: b, nss: f.analyzed ? round1(((f.pos - f.neg) / f.analyzed) * 100) : 0, volume: f.analyzed };
    });

    return {
        evolution,
        totals: {
            followers, growth, interactions, posts, analyzed, views,
            nss: analyzed ? round1(((pos - neg) / analyzed) * 100) : null,
            er: erDen ? round1(erNum / erDen) : null,
        },
    };
}

// ── Miniatura con fallback (CDN caduca / 403) ─────────────────────────────────
export function Thumb({ src, className }) {
    const [ok, setOk] = useState(true);
    return src && ok
        ? <img className={className} src={proxyImage(src)} alt="" loading="lazy" onError={() => setOk(false)} />
        : <div className={`gd-thumb-ph ${className || ''}`}><FaImage /></div>;
}

// ── Tarjeta de publicación con miniatura ─────────────────────────────────────
export function TopPostCard({ post, onOpen }) {
    const meta = PLAT[post._platform] || {};
    const PIcon = meta.Icon || FaGlobe;
    return (
        <button className="gd-top-card" onClick={() => onOpen(post)}>
            <div className="gd-top-thumb">
                <Thumb src={post.thumbnail} />
                {post._kind && <span className="gd-top-kind" style={{ background: KIND[post._kind].color }}>{KIND[post._kind].label}</span>}
                {post.nss != null && <span className="gd-top-nss" style={{ background: nssColor(post.nss) }}>NSS {post.nss}</span>}
            </div>
            <div className="gd-top-body">
                <span className="gd-top-handle"><PIcon style={{ color: meta.color }} /> {acctHandle({ username: post._handle, platform: post._platform, track_by: post._track_by })}</span>
                <div className="gd-top-metrics">
                    <span className="gd-top-er">ER {post.er ?? '—'}%</span>
                    <span><FaHeart /> {fmt(post.likes)}</span>
                    <span><FaCommentDots /> {fmt(post.comments_count)}</span>
                </div>
            </div>
        </button>
    );
}

// ── Modal de detalle de una publicación (solo lectura) ────────────────────────
export function PostModal({ username, post, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imgOk, setImgOk] = useState(true);
    useEffect(() => {
        let on = true;
        setLoading(true);
        socialApi.postDetail(username, post.id)
            .then((r) => { if (on) setDetail(r.detail); })
            .catch(() => {})
            .finally(() => { if (on) setLoading(false); });
        return () => { on = false; };
    }, [username, post.id]);

    const pv = detail?.post || post;
    const sent = detail?.sentiment;
    const comments = detail?.comments || [];
    const total = sent ? ((sent.pos + sent.neu + sent.neg) || 1) : 1;
    const pct = (x) => Math.round((x / total) * 100);
    const netName = post._platform === 'facebook' ? 'Facebook' : post._platform === 'tiktok' ? 'TikTok' : 'Instagram';

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="sa-modal-grid">
                    <div className="sa-modal-preview">
                        {pv.thumbnail && imgOk
                            ? <img src={proxyImage(pv.thumbnail)} alt="" onError={() => setImgOk(false)} />
                            : <div className="gd-thumb-ph big"><FaImage /></div>}
                        <div className="sa-modal-meta">
                            <span>{(pv.posted_at || '').slice(0, 10)} · {pv.content_type || 'Post'}{pv.is_paid ? ' 💰' : ''}</span>
                            <span><FaHeart /> {fmt(pv.likes)} · <FaCommentDots /> {fmt(pv.comments_count)}
                                {pv.views != null ? <> · <FaPlay /> {fmt(pv.views)}</> : null}</span>
                            {post._handle && <span style={{ color: '#64748b' }}>{acctHandle({ username: post._handle, platform: post._platform, track_by: post._track_by })}</span>}
                            {pv.url && <a href={pv.url} target="_blank" rel="noreferrer">Ver en {netName} <FaExternalLinkAlt /></a>}
                        </div>
                        <p className="sa-modal-caption">{pv.caption || '(sin descripción)'}</p>
                    </div>
                    <div className="sa-modal-analysis">
                        {loading && <p className="gd-subtitle">Cargando…</p>}
                        {sent ? (
                            <div className="sa-modal-sent">
                                <div className="sa-sent-head">
                                    <span className="sa-sent-nss" style={{ color: nssColor(sent.nss) }}>NSS {sent.nss}</span>
                                    <span className="sa-sent-sub">{sent.comments_analyzed} comentarios · polarización {sent.polarization}</span>
                                </div>
                                <div className="sa-sent-bar">
                                    <div style={{ width: `${pct(sent.pos)}%`, background: sentDot.pos }} />
                                    <div style={{ width: `${pct(sent.neu)}%`, background: sentDot.neu }} />
                                    <div style={{ width: `${pct(sent.neg)}%`, background: sentDot.neg }} />
                                </div>
                                {sent.summary && <p className="sa-sent-summary">{sent.summary}</p>}
                            </div>
                        ) : (!loading && <p className="gd-subtitle">Sin análisis de sentimiento para esta publicación. Analízalo desde <b>Redes Sociales</b>.</p>)}
                        {comments.length > 0 && (
                            <div className="sa-comments">
                                <h5>Comentarios ({comments.length})</h5>
                                {comments.map((c, i) => (
                                    <div key={i} className="sa-comment">
                                        {c.sentiment && <span className="sa-comment-dot" style={{ background: sentDot[c.sentiment] }} title={c.theme || ''} />}
                                        <div className="sa-comment-body">
                                            <span className="sa-comment-user">@{c.commenter || 'usuario'}</span>
                                            <span className="sa-comment-text">{c.text}</span>
                                            <span className="sa-comment-meta"><FaHeart /> {fmt(c.likes)}{c.theme ? ` · ${c.theme}` : ''}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
